"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.ViewportList = void 0;
var jsx_runtime_1 = require("react/jsx-runtime");
var react_1 = require("react");
var IS_SSR = typeof window === 'undefined';
var IS_TOUCH_DEVICE = IS_SSR
    ? false
    : (function () {
        try {
            return 'ontouchstart' in window || navigator.maxTouchPoints;
        }
        catch (error) {
            return false;
        }
    })();
var IS_OVERFLOW_ANCHOR_SUPPORTED = IS_SSR
    ? false
    : (function () {
        try {
            return window.CSS.supports('overflow-anchor: auto');
        }
        catch (error) {
            return false;
        }
    })();
var SHOULD_DELAY_SCROLL = IS_TOUCH_DEVICE && !IS_OVERFLOW_ANCHOR_SUPPORTED; // mobile Safari
var PROP_NAME_FOR_Y_AXIS = {
    top: 'top',
    bottom: 'bottom',
    clientHeight: 'clientHeight',
    scrollHeight: 'scrollHeight',
    scrollTop: 'scrollTop',
    overflowY: 'overflowY',
    height: 'height',
    minHeight: 'minHeight',
    maxHeight: 'maxHeight',
    marginTop: 'marginTop',
};
var PROP_NAME_FOR_X_AXIS = {
    top: 'left',
    bottom: 'right',
    scrollHeight: 'scrollWidth',
    clientHeight: 'clientWidth',
    scrollTop: 'scrollLeft',
    overflowY: 'overflowX',
    minHeight: 'minWidth',
    height: 'width',
    maxHeight: 'maxWidth',
    marginTop: 'marginLeft',
};
var getStyle = function (propName, size, marginTop) {
    var _a;
    if (marginTop === void 0) { marginTop = 0; }
    return (_a = {
            padding: 0,
            margin: 0,
            border: 'none',
            visibility: 'hidden',
            // We need to off 'overflow-anchor' for spacers. Otherwise, it may cause unnecessary scroll jumps.
            overflowAnchor: 'none'
        },
        _a[propName.minHeight] = size,
        _a[propName.height] = size,
        _a[propName.maxHeight] = size,
        _a[propName.marginTop] = marginTop,
        _a);
};
var normalizeValue = function (min, value, max) {
    if (max === void 0) { max = Infinity; }
    return Math.max(Math.min(value, max), min);
};
var useIsomorphicLayoutEffect = IS_SSR ? react_1.useEffect : react_1.useLayoutEffect;
var useAnimationFrame = function (func) {
    var ref = (0, react_1.useRef)(func);
    useIsomorphicLayoutEffect(function () {
        ref.current = func;
    }, [func]);
    useIsomorphicLayoutEffect(function () {
        var frameId;
        var frame = function () {
            frameId = requestAnimationFrame(frame);
            ref.current();
        };
        frame();
        return function () { return cancelAnimationFrame(frameId); };
    }, []);
};
var getDiff = function (value1, value2, step) { return Math.ceil(Math.abs(value1 - value2) / step); };
var ViewportListInner = function (_a, ref) {
    var _b = _a.viewportRef, viewportRef = _b === void 0 ? { current: IS_SSR ? null : document.documentElement } : _b, _c = _a.items, items = _c === void 0 ? [] : _c, _d = _a.itemMinSize, itemMinSize = _d === void 0 ? 0 : _d, _e = _a.margin, margin = _e === void 0 ? -1 : _e, _f = _a.overscan, overscan = _f === void 0 ? 1 : _f, _g = _a.axis, axis = _g === void 0 ? 'y' : _g, _h = _a.initialIndex, initialIndex = _h === void 0 ? -1 : _h, _j = _a.initialAlignToTop, initialAlignToTop = _j === void 0 ? true : _j, _k = _a.initialOffset, initialOffset = _k === void 0 ? 0 : _k, children = _a.children, onViewportIndexesChange = _a.onViewportIndexesChange, _l = _a.overflowAnchor, overflowAnchor = _l === void 0 ? 'auto' : _l, _m = _a.withCache, withCache = _m === void 0 ? true : _m, _o = _a.scrollThreshold, scrollThreshold = _o === void 0 ? 0 : _o, _p = _a.spacerElement, spacerElement = _p === void 0 ? 'div' : _p, _q = _a.spacerStyle, spacerStyle = _q === void 0 ? {} : _q;
    var propName = axis === 'y' ? PROP_NAME_FOR_Y_AXIS : PROP_NAME_FOR_X_AXIS;
    var maxIndex = items.length - 1;
    var _r = (0, react_1.useState)(function () { return [
        normalizeValue(0, itemMinSize),
        normalizeValue(-1, margin),
    ]; }), _s = _r[0], itemHeight = _s[0], itemMargin = _s[1], setItemDimensions = _r[1];
    var itemHeightWithMargin = normalizeValue(0, itemHeight + itemMargin);
    var overscanSize = normalizeValue(0, Math.ceil(overscan * itemHeightWithMargin));
    var _t = (0, react_1.useState)([initialIndex, initialIndex]), indexes = _t[0], setIndexes = _t[1];
    var startIndex = (indexes[0] = normalizeValue(0, indexes[0], maxIndex));
    var endIndex = (indexes[1] = normalizeValue(startIndex, indexes[1], maxIndex));
    var topSpacerRef = (0, react_1.useRef)(null);
    var bottomSpacerRef = (0, react_1.useRef)(null);
    var cacheRef = (0, react_1.useRef)([]);
    var scrollToIndexRef = (0, react_1.useRef)(initialIndex >= 0 ? { index: initialIndex, alignToTop: initialAlignToTop, offset: initialOffset } : null);
    var marginTopRef = (0, react_1.useRef)(0);
    var viewportIndexesRef = (0, react_1.useRef)([-1, -1]);
    var anchorElementRef = (0, react_1.useRef)(null);
    var anchorIndexRef = (0, react_1.useRef)(-1);
    var scrollToIndexTimeoutId = (0, react_1.useRef)(null);
    var topSpacerStyle = (0, react_1.useMemo)(function () { return (__assign(__assign({}, spacerStyle), getStyle(propName, 
    // Array.prototype.reduce() runs only for initialized items.
    cacheRef.current
        .slice(0, startIndex)
        .reduce(function (sum, next) { return sum + (next - itemHeight); }, startIndex * itemHeightWithMargin), marginTopRef.current))); }, [propName, startIndex, itemHeightWithMargin, itemHeight, spacerStyle]);
    var bottomSpacerStyle = (0, react_1.useMemo)(function () { return (__assign(__assign({}, spacerStyle), getStyle(propName, 
    // Array.prototype.reduce() runs only for initialized items.
    cacheRef.current
        .slice(endIndex + 1, maxIndex + 1)
        .reduce(function (sum, next) { return sum + (next - itemHeight); }, itemHeightWithMargin * (maxIndex - endIndex))))); }, [propName, endIndex, maxIndex, itemHeightWithMargin, itemHeight, spacerStyle]);
    var scrollTopRef = (0, react_1.useRef)(null);
    useAnimationFrame(function () {
        var _a, _b;
        var viewport = viewportRef.current;
        var topSpacer = topSpacerRef.current;
        var bottomSpacer = bottomSpacerRef.current;
        if (!viewport || !topSpacer || !bottomSpacer) {
            return;
        }
        var topElement = topSpacer.nextSibling;
        var bottomElement = bottomSpacer.previousSibling;
        var topSecondElement = topElement === bottomSpacer ? bottomSpacer : topElement.nextSibling;
        var bottomSecondElement = bottomElement === topSpacer ? topSpacer : bottomElement.previousSibling;
        var viewportRect = viewport.getBoundingClientRect();
        var topSpacerRect = topSpacer.getBoundingClientRect();
        var bottomSpacerRect = bottomSpacer.getBoundingClientRect();
        var limits = (_a = {},
            _a[propName.top] = viewport === document.documentElement ? 0 : viewportRect[propName.top],
            _a[propName.bottom] = viewport === document.documentElement
                ? document.documentElement.clientHeight
                : viewportRect[propName.bottom],
            _a);
        var limitsWithOverscanSize = (_b = {},
            _b[propName.top] = limits[propName.top] - overscanSize,
            _b[propName.bottom] = limits[propName.bottom] + overscanSize,
            _b);
        if ((marginTopRef.current < 0 &&
            topSpacerRect[propName.top] - marginTopRef.current >= limitsWithOverscanSize[propName.top]) ||
            (marginTopRef.current > 0 && topSpacerRect[propName.top] >= limitsWithOverscanSize[propName.top]) ||
            (marginTopRef.current && scrollToIndexRef.current)) {
            topSpacer.style[propName.marginTop] = '0px';
            viewport.style[propName.overflowY] = 'hidden';
            viewport[propName.scrollTop] += -marginTopRef.current;
            viewport.style[propName.overflowY] = '';
            marginTopRef.current = 0;
            return;
        }
        if (itemHeight === 0 || itemMargin === -1) {
            if (topElement === bottomSpacer) {
                return;
            }
            var nextItemHeight = itemHeight === 0 ? topElement[propName.clientHeight] : itemHeight;
            var nextItemMargin = itemMargin === -1
                ? bottomSpacerRect[propName.top] - topSpacerRect[propName.bottom] - nextItemHeight
                : itemMargin;
            setItemDimensions([nextItemHeight, nextItemMargin]);
            return;
        }
        var averageSize = Math.ceil((bottomSpacerRect[propName.top] - topSpacerRect[propName.bottom]) / (endIndex + 1 - startIndex));
        if (scrollToIndexTimeoutId.current) {
            return;
        }
        if (scrollToIndexRef.current) {
            var targetIndex = normalizeValue(0, scrollToIndexRef.current.index, maxIndex);
            if (targetIndex < startIndex || targetIndex > endIndex) {
                setIndexes([targetIndex, targetIndex]);
                return;
            }
            var index = startIndex;
            var element_1 = topElement;
            var _loop_1 = function () {
                if (index === targetIndex) {
                    var alignToTop_1 = scrollToIndexRef.current.alignToTop;
                    var offset_1 = scrollToIndexRef.current.offset;
                    scrollToIndexRef.current = null;
                    var scrollToElement = function () {
                        var elementRect = element_1.getBoundingClientRect();
                        if (alignToTop_1) {
                            viewport[propName.scrollTop] += elementRect[propName.top] - limits[propName.top] + offset_1;
                        }
                        else {
                            viewport[propName.scrollTop] +=
                                elementRect[propName.bottom] -
                                    limits[propName.top] -
                                    viewport[propName.clientHeight] +
                                    offset_1;
                        }
                        scrollToIndexTimeoutId.current = null;
                    };
                    if (SHOULD_DELAY_SCROLL) {
                        scrollToIndexTimeoutId.current = setTimeout(scrollToElement, 30);
                    }
                    else {
                        scrollToElement();
                    }
                    return "break";
                }
                index++;
                element_1 = element_1.nextSibling;
            };
            while (element_1 && element_1 !== bottomSpacer) {
                var state_1 = _loop_1();
                if (state_1 === "break")
                    break;
            }
            return;
        }
        if (scrollTopRef.current === null) {
            scrollTopRef.current = viewport.scrollTop;
        }
        else if (scrollTopRef.current !== viewport.scrollTop) {
            var diff = Math.abs(viewport.scrollTop - scrollTopRef.current);
            scrollTopRef.current = viewport.scrollTop;
            if (scrollThreshold > 0 && diff > scrollThreshold) {
                return;
            }
        }
        var isAllAboveTop = topSpacerRect[propName.bottom] > limitsWithOverscanSize[propName.bottom];
        var isAllBelowBottom = bottomSpacerRect[propName.top] < limitsWithOverscanSize[propName.top];
        var isTopBelowTop = !isAllAboveTop &&
            !isAllBelowBottom &&
            topSpacerRect[propName.bottom] > limitsWithOverscanSize[propName.top];
        var isBottomAboveBottom = !isAllAboveTop &&
            !isAllBelowBottom &&
            bottomSpacerRect[propName.top] < limitsWithOverscanSize[propName.bottom];
        var isBottomSecondAboveTop = !isAllAboveTop &&
            !isAllBelowBottom &&
            bottomSecondElement.getBoundingClientRect()[propName.bottom] > limitsWithOverscanSize[propName.bottom];
        var isTopSecondAboveTop = !isAllAboveTop &&
            !isAllBelowBottom &&
            topSecondElement.getBoundingClientRect()[propName.top] < limitsWithOverscanSize[propName.top];
        var nextStartIndex = startIndex;
        var nextEndIndex = endIndex;
        if (isAllAboveTop) {
            nextStartIndex -= getDiff(topSpacerRect[propName.bottom], limitsWithOverscanSize[propName.top], averageSize);
            nextEndIndex -= getDiff(bottomSpacerRect[propName.top], limitsWithOverscanSize[propName.bottom], averageSize);
        }
        if (isAllBelowBottom) {
            nextEndIndex += getDiff(bottomSpacerRect[propName.top], limitsWithOverscanSize[propName.bottom], averageSize);
            nextStartIndex += getDiff(topSpacerRect[propName.bottom], limitsWithOverscanSize[propName.top], averageSize);
        }
        if (isTopBelowTop) {
            nextStartIndex -= getDiff(topSpacerRect[propName.bottom], limitsWithOverscanSize[propName.top], averageSize);
        }
        if (isBottomAboveBottom) {
            nextEndIndex += getDiff(bottomSpacerRect[propName.top], limitsWithOverscanSize[propName.bottom], averageSize);
        }
        if (isBottomSecondAboveTop) {
            var index = endIndex;
            var element = bottomElement;
            while (element && element !== topSpacer) {
                if (element.getBoundingClientRect()[propName.bottom] <= limitsWithOverscanSize[propName.bottom]) {
                    nextEndIndex = index + 1;
                    break;
                }
                index--;
                element = element.previousSibling;
            }
        }
        if (isTopSecondAboveTop) {
            var index = startIndex;
            var element = topElement;
            while (element && element !== bottomSpacer) {
                if (element.getBoundingClientRect()[propName.top] >= limitsWithOverscanSize[propName.top]) {
                    nextStartIndex = index - 1;
                    break;
                }
                index++;
                element = element.nextSibling;
            }
        }
        if (onViewportIndexesChange) {
            var index = startIndex;
            var element = topElement;
            var startViewportIndex = startIndex;
            while (element && element !== bottomSpacer) {
                if (element.getBoundingClientRect()[propName.bottom] > limits[propName.top]) {
                    startViewportIndex = index;
                    break;
                }
                index++;
                element = element.nextSibling;
            }
            index = endIndex;
            element = bottomElement;
            var endViewportIndex = endIndex;
            while (element && element !== topSpacer) {
                if (element.getBoundingClientRect()[propName.top] < limits[propName.bottom]) {
                    endViewportIndex = index;
                    break;
                }
                index--;
                element = element.previousSibling;
            }
            if (startViewportIndex !== viewportIndexesRef.current[0] ||
                endViewportIndex !== viewportIndexesRef.current[1]) {
                viewportIndexesRef.current = [startViewportIndex, endViewportIndex];
                onViewportIndexesChange(viewportIndexesRef.current);
            }
        }
        nextStartIndex = normalizeValue(0, nextStartIndex, maxIndex);
        nextEndIndex = normalizeValue(nextStartIndex, nextEndIndex, maxIndex);
        if (nextStartIndex === startIndex && nextEndIndex === endIndex) {
            return;
        }
        if (nextStartIndex !== startIndex) {
            var anchorElement = null;
            var anchorElementIndex = -1;
            if (startIndex >= nextStartIndex && startIndex <= nextEndIndex) {
                anchorElement = topElement;
                anchorElementIndex = startIndex;
            }
            else if (nextStartIndex >= startIndex && nextStartIndex <= endIndex) {
                var index = startIndex;
                var element = topElement;
                while (element && element !== bottomSpacer) {
                    if (index === nextStartIndex) {
                        anchorElement = element;
                        anchorElementIndex = index;
                        break;
                    }
                    if (withCache && element[propName.clientHeight] !== itemHeight) {
                        cacheRef.current[index] = element[propName.clientHeight];
                    }
                    index++;
                    element = element.nextSibling;
                }
            }
            anchorElementRef.current = anchorElement;
            anchorIndexRef.current = anchorElementIndex;
        }
        setIndexes([nextStartIndex, nextEndIndex]);
    });
    var anchorScrollTopOnRender;
    var anchorHeightOnRender;
    if (anchorElementRef.current && viewportRef.current && topSpacerRef.current) {
        anchorScrollTopOnRender = viewportRef.current[propName.scrollTop];
        anchorHeightOnRender =
            anchorElementRef.current.getBoundingClientRect()[propName.top] -
                topSpacerRef.current.getBoundingClientRect()[propName.top];
    }
    useIsomorphicLayoutEffect(function () {
        anchorElementRef.current = null;
        var anchorIndex = anchorIndexRef.current;
        anchorIndexRef.current = -1;
        var viewport = viewportRef.current;
        var topSpacer = topSpacerRef.current;
        var bottomSpacer = bottomSpacerRef.current;
        if ((IS_OVERFLOW_ANCHOR_SUPPORTED && overflowAnchor !== 'none') ||
            anchorIndex === -1 ||
            !viewport ||
            !topSpacer ||
            !bottomSpacer ||
            anchorScrollTopOnRender === undefined ||
            anchorHeightOnRender === undefined ||
            anchorScrollTopOnRender !== viewportRef.current[propName.scrollTop]) {
            return;
        }
        var anchorElement = null;
        var index = startIndex;
        var element = topSpacer.nextSibling;
        while (element && element !== bottomSpacer) {
            if (index === anchorIndex) {
                anchorElement = element;
                break;
            }
            index++;
            element = element.nextSibling;
        }
        if (!anchorElement) {
            return;
        }
        var offset = anchorElement.getBoundingClientRect()[propName.top] -
            topSpacer.getBoundingClientRect()[propName.top] -
            anchorHeightOnRender;
        if (!offset) {
            return;
        }
        if (IS_TOUCH_DEVICE) {
            marginTopRef.current -= offset;
            topSpacerRef.current.style[propName.marginTop] = marginTopRef.current + "px";
            return;
        }
        viewportRef.current[propName.scrollTop] += offset;
    }, [startIndex]);
    (0, react_1.useEffect)(function () { return function () {
        if (scrollToIndexTimeoutId.current) {
            clearTimeout(scrollToIndexTimeoutId.current);
        }
    }; }, []);
    (0, react_1.useImperativeHandle)(ref, function () { return ({
        scrollToIndex: function (index, alignToTop, offset) {
            if (index === void 0) { index = -1; }
            if (alignToTop === void 0) { alignToTop = true; }
            if (offset === void 0) { offset = 0; }
            scrollToIndexRef.current = { index: index, alignToTop: alignToTop, offset: offset };
        },
    }); }, []);
    return ((0, jsx_runtime_1.jsxs)(react_1.Fragment, { children: [(0, react_1.createElement)(spacerElement, { ref: topSpacerRef, style: topSpacerStyle }), items.slice(startIndex, endIndex + 1).map(function (item, index) { return children(item, startIndex + index, items); }), (0, react_1.createElement)(spacerElement, { ref: bottomSpacerRef, style: bottomSpacerStyle })] }, void 0));
};
exports.ViewportList = (0, react_1.forwardRef)(ViewportListInner);
// eslint-disable-next-line import/no-default-export
exports.default = exports.ViewportList;
